import { useCallback, useEffect, useRef, useState } from "react";
import { UploaderItemProps } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { useCheckLimits } from "../../../../hooks/useCheckLimits";
import { FileURLInfo } from "../../DialogComponents/FileUploader/types";

import { useFileLoader } from "../../Import/hooks/useFileLoader";
import { api } from "../../../../evergis/api";
import { DEFAULT_UPLOAD_MAX_FILE_SIZE } from "../constants";
import { UploadState } from "../types";

// eslint-disable-next-line max-lines-per-function
export const useUploadPolling = (
  inputFiles: Array<File | FileURLInfo>,
  onDelete: (name: string) => void,
): UploadState => {
  const { t } = useTranslation("dialogs");
  const [file, { upload, uploadFileURL }] = useFileLoader();
  const [uploadInProgress, setInProgress] = useState(false);
  const [uploadDone, setUploadDone] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploaderItemProps[]>([]);
  const [uploadError, setUploadError] = useState<string>();
  const [isDeleting, setIsDeleting] = useState(false);
  const prevFiles = useRef(inputFiles);
  const { limits } = useCheckLimits();

  const uploadMaxFileSize = limits?.maxUploadContentSize || DEFAULT_UPLOAD_MAX_FILE_SIZE;

  const uploadNext = useCallback(
    (uploadingFiles: UploaderItemProps[]) => {
      const nextFile = uploadingFiles.find(item => !item.progress && !item.done);

      if (!nextFile) return;

      const fileItem = inputFiles.find(
        item => ((item as File).name || (item as FileURLInfo).fileName) === nextFile.name,
      );

      try {
        if (fileItem instanceof File) {
          upload(fileItem, true);
        } else {
          uploadFileURL(fileItem, fileItem.url, true);
        }
      } catch (error) {
        setUploadError(error);
      }
    },
    [inputFiles, upload],
  );

  const uploadDelete = useCallback(
    (name: string) => {
      setIsDeleting(true);
      onDelete(name);
      api.file.deleteFile({ fileid: name });
      setUploadedFiles(uploadedFiles.filter(item => item.name !== name));
      prevFiles.current = prevFiles.current.filter(
        item => ((item as File).name || (item as FileURLInfo).fileName) !== name,
      );
    },
    [onDelete, uploadedFiles],
  );

  useEffect(() => {
    if (isDeleting) {
      setIsDeleting(false);

      return;
    }

    const filteredFiles =
      inputFiles?.filter(
        item =>
          !uploadedFiles.some(uploaded => uploaded.name === ((item as File).name || (item as FileURLInfo).fileName)),
      ) || [];

    if (!filteredFiles.length) return;

    const newFiles = filteredFiles.map(item => ({
      name: (item as File).name || (item as FileURLInfo).fileName,
      id: (item as File).name || (item as FileURLInfo).fileName,
      total: (item as File).size || (item as FileURLInfo).contentLength,
      progress: 0,
      done: false,
      error: file.size > uploadMaxFileSize && uploadMaxFileSize !== -1 ? t("import.fileSizeExceeded") : "",
    }));

    const uploadFiles = [...uploadedFiles, ...newFiles];

    setInProgress(true);
    uploadNext(newFiles);

    prevFiles.current = [...prevFiles.current, ...inputFiles];
    setUploadedFiles(uploadFiles);
  }, [inputFiles, uploadNext, uploadedFiles]);

  useEffect(() => {
    if (!file?.name || file?.loading) return;

    const index = uploadedFiles.findIndex(item => item.name === file.name);

    uploadedFiles[index] = {
      ...uploadedFiles[index],
      progress: file.size,
      done: true,
      error: file.size > uploadMaxFileSize && uploadMaxFileSize !== -1 ? t("import.fileSizeExceeded") : "",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      resourceId: file.resourceId,
    };

    setProcessed(processed + 1);
    setUploadedFiles([...uploadedFiles]);
    uploadNext([...uploadedFiles]);
  }, [file?.name, file?.loading]);

  useEffect(() => {
    if (uploadedFiles.length && uploadedFiles.length === processed) {
      setInProgress(false);
      setUploadDone(true);
    }
  }, [processed]);

  return { uploadInProgress, uploadDone, uploadedFiles, uploadError, uploadDelete };
};
